import React from 'react';
import { RiErrorWarningLine } from 'react-icons/ri';
import ValidatedInput from '../ui/ValidatedInput';
import ValidatedTextarea from '../ui/ValidatedTextarea';
import type { SupplierForm } from '../../services/supplierService';

/**
 * ช่องกรอกผู้จำหน่าย — ยกมาจากโมดัลเดิมใน SupplierPage ครบทุกช่อง (มติ PO 28 ต.ค. 2026 → หน้าเต็ม)
 * KD3 S2121 (BUG-S2121-001): ทุกช่องที่กรอกได้ต้องมี error={formErrors.<name>} — 422 จาก backend
 * (เบอร์โทรผิดรูปแบบ / เลขภาษียาวเกิน) ต้องแดงใต้ช่อง ไม่ใช่แค่ toast
 */
export interface SupplierFormFieldsProps {
  formData: SupplierForm;
  setFormData: React.Dispatch<React.SetStateAction<SupplierForm>>;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  codeError: string;
}

const SupplierFormFields: React.FC<SupplierFormFieldsProps> = ({
  formData, setFormData, formErrors, setFormErrors, codeError,
}) => {
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="min-w-0">
          <ValidatedInput
            label="รหัสผู้จำหน่าย"
            name="code"
            value={formData.code}
            onChange={handleFormChange}
            error={formErrors.code}
            required={true}
            disabled={true}
            placeholder="รหัสจะสร้างอัตโนมัติ"
          />
          {codeError && (
            <p className="mt-1 text-sm text-danger-600 flex items-center">
              <RiErrorWarningLine className="w-4 h-4 mr-1 flex-shrink-0" />
              {codeError}
            </p>
          )}
        </div>
        <ValidatedInput
          label="ชื่อผู้จำหน่าย"
          name="name"
          value={formData.name}
          onChange={handleFormChange}
          error={formErrors.name}
          required={true}
          placeholder="เช่น บริษัท ซันมิ ประเทศไทย จำกัด"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ValidatedInput
          label="ผู้ติดต่อ"
          name="contact_person"
          value={formData.contact_person}
          onChange={handleFormChange}
          error={formErrors.contact_person}
          placeholder="ชื่อผู้ติดต่อ"
        />
        <ValidatedInput
          label="เบอร์โทรศัพท์"
          name="phone"
          value={formData.phone}
          onChange={handleFormChange}
          error={formErrors.phone}
          placeholder="เช่น 02-xxx-xxxx"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ValidatedInput
          label="อีเมล"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleFormChange}
          error={formErrors.email}
          placeholder="example@company.com"
        />
        <ValidatedInput
          label="เลขประจำตัวผู้เสียภาษี"
          name="tax_id"
          value={formData.tax_id}
          onChange={handleFormChange}
          error={formErrors.tax_id}
          placeholder="เช่น 0105500000001"
        />
      </div>

      <ValidatedTextarea
        label="ที่อยู่"
        name="address"
        value={formData.address}
        onChange={handleFormChange}
        error={formErrors.address}
        rows={2}
        placeholder="ที่อยู่สำหรับติดต่อ"
      />

      <ValidatedTextarea
        label="หมายเหตุ"
        name="notes"
        value={formData.notes}
        onChange={handleFormChange}
        error={formErrors.notes}
        rows={2}
        placeholder="หมายเหตุเพิ่มเติม"
      />

      <div>
        <label htmlFor="supplier-is-active" className="block text-sm font-medium text-secondary-700 mb-1">
          สถานะ
        </label>
        <select
          id="supplier-is-active"
          aria-label="สถานะ"
          name="is_active"
          value={formData.is_active ? 'true' : 'false'}
          onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}
          className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="true">ใช้งาน</option>
          <option value="false">ไม่ใช้งาน</option>
        </select>
      </div>
    </div>
  );
};

export default SupplierFormFields;
